import { Button, PageTitle } from '@/components';
import { useGetCompany } from '@/services';
import {
	Activity,
	AlertCircle,
	Check,
	Copy,
	CreditCard,
	Crown,
	ExternalLink,
	Users,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { SubscribePlanModal } from '../components/SubscribePlanModal';
import { useGetEmployeeStatsByCompany } from '@/services/queries/employee';

export const Finance = () => {
	const { id } = useParams<{ id: string }>();
	const { data: company } = useGetCompany(id || '', {
		refetchOnWindowFocus: true,
	});
	const subscriptions = company?.company_plan_subscription ?? [];
	const activeSubscription = subscriptions.find((s) => s.status === 'ACTIVE');
	const subscription = activeSubscription ?? subscriptions[0];
	const plan = subscription?.company_plan;

	const isPaid = activeSubscription?.status === 'ACTIVE';

	const currency = plan?.currency || 'BRL';
	const baseCost = plan ? Number(plan.base_cost || 0) : 0;
	const maintenanceCost = 0;
	const formatMoney = (value: number) =>
		new Intl.NumberFormat('pt-BR', {
			style: 'currency',
			currency,
		}).format(value);

	const [isSubscribePlanModalOpen, setIsSubscribePlanModalOpen] =
		useState(false);

	const [copiedPaymentLink, setCopiedPaymentLink] = useState(false);

	const totalEmployees = company?.employees?.length || 0;

	const subsidyPerEmployee = plan
		? Number(plan.subsidy_per_employee || 0)
		: 0;

	const totalValue =
		baseCost +
		maintenanceCost +
		subsidyPerEmployee * (plan?.max_employees ?? 0);

	const formattedValue = formatMoney(totalValue);
	const { data: employeeStats } = useGetEmployeeStatsByCompany(id || '');

	return (
		<section className="flex animate-fade-in flex-col gap-6">
			<PageTitle text="Financeiro da empresa" />
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
				{/* Linha 1 - Plano */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Plano
						</span>
						<div className="rounded-xl bg-purple-50 p-2.5">
							<Crown size={18} className="text-purple-500" />
						</div>
					</div>
					<h3 className="line-clamp-2 text-xl font-black leading-snug text-stone-900">
						{plan?.name || 'Sem plano assinado'}
					</h3>
					<div className="mt-2 space-y-1 text-[10px] text-stone-400">
						<div>
							{plan ? `${plan.max_employees} colaboradores` : '—'}
						</div>
						<div>
							{plan
								? `Base + Manutenção: ${formatMoney(
										baseCost + maintenanceCost,
								  )}/mês`
								: '—'}
						</div>
					</div>
				</div>

				{/* Linha 1 - Situação */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Situação
						</span>
						<div className="rounded-xl bg-blue-50 p-2.5">
							<Activity size={18} className="text-blue-500" />
						</div>
					</div>
					<h3 className="text-xl font-black leading-none text-stone-900">
						{isPaid ? 'Ativo' : 'Inativo'}
					</h3>
					<span
						className={`mt-1.5 inline-block text-[10px] font-black uppercase tracking-widest ${
							isPaid ? 'text-emerald-500' : 'text-rose-500'
						}`}
					>
						{isPaid ? 'Operacional' : 'Suspenso'}
					</span>
				</div>

				{/* Linha 1 - Cobertura */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Cobertura
						</span>
						<div className="rounded-xl bg-stone-50 p-2.5">
							<Users size={18} className="text-stone-600" />
						</div>
					</div>
					<h3 className="text-xl font-black leading-none text-stone-900">
						{plan?.max_employees
							? `${totalEmployees}/${plan.max_employees}`
							: `${totalEmployees}`}
					</h3>
					<span className="mt-1.5 block text-[10px] font-black uppercase tracking-widest text-stone-400">
						{plan?.max_employees
							? `Uso de ${Math.min(
									100,
									Math.round(
										(totalEmployees /
											(plan.max_employees || 1)) *
											100,
									),
							  )}% da capacidade`
							: 'Colaboradores cadastrados no plano'}
					</span>
				</div>

				{/* Linha 1 - Pagamento */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Pagamento
						</span>
						<div
							className={`rounded-xl p-2.5 ${
								isPaid ? 'bg-stone-50' : 'bg-rose-50'
							}`}
						>
							<AlertCircle
								size={18}
								className={
									isPaid ? 'text-stone-900' : 'text-rose-500'
								}
							/>
						</div>
					</div>
					<h3 className="text-xl font-black leading-none text-stone-900">
						{isPaid ? 'Pago' : 'Pendente'}
					</h3>
					<span
						className={`mt-1.5 inline-block text-[10px] font-black uppercase tracking-widest ${
							isPaid ? 'text-emerald-500' : 'text-rose-500'
						}`}
					>
						{isPaid ? 'Sem pendências' : 'Aguardando'}
					</span>
					{/* Ação de plano: só quando NÃO está pago */}
					{!isPaid && subscription?.stripe_checkout_session_url && (
						<div className="mt-3 flex flex-col gap-2">
							<a
								href={subscription.stripe_checkout_session_url}
								target="_blank"
								rel="noopener noreferrer"
								className="flex w-full items-center justify-center gap-2 rounded-full bg-black px-4 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-neutral-800"
							>
								<ExternalLink className="h-4 w-4 stroke-[3]" />
								Ir para pagamento
							</a>
							<Button
								text={
									copiedPaymentLink
										? 'Link copiado'
										: 'Copiar link de pagamento'
								}
								startIcon={
									copiedPaymentLink ? (
										<Check className="h-4 w-4 text-emerald-500" />
									) : (
										<Copy className="h-4 w-4" />
									)
								}
								className="flex w-full items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 shadow-sm transition-all hover:bg-stone-50"
								onClick={async () => {
									if (
										!subscription.stripe_checkout_session_url
									)
										return;
									try {
										await navigator.clipboard.writeText(
											subscription.stripe_checkout_session_url,
										);
										setCopiedPaymentLink(true);
										setTimeout(
											() => setCopiedPaymentLink(false),
											2000,
										);
									} catch {
										setCopiedPaymentLink(false);
									}
								}}
							/>
						</div>
					)}
					{!isPaid && !subscription?.stripe_checkout_session_url && (
						<Button
							text="Assinar plano"
							disabled={!company?.company_club?.length}
							className="mt-4 w-full justify-center rounded-full bg-black px-4 py-2.5 text-sm font-bold shadow-md hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
							startIcon={
								<CreditCard className="size-5 stroke-[3] text-white" />
							}
							onClick={() => setIsSubscribePlanModalOpen(true)}
						/>
					)}
					{isPaid && subscription?.stripe_checkout_session_url && (
						<a
							href={subscription.stripe_checkout_session_url}
							target="_blank"
							rel="noopener noreferrer"
							className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2.5 text-sm font-bold text-stone-700 shadow-sm transition-all hover:bg-stone-50"
						>
							<ExternalLink className="h-4 w-4 stroke-[3]" />
							Gerenciar plano
						</a>
					)}
				</div>
			</div>
			<div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-4">
				{/* Linha 2 - Preço base */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Preço base
						</span>
						<div className="rounded-xl bg-stone-50 p-2.5">
							<CreditCard size={18} className="text-stone-700" />
						</div>
					</div>
					<h3 className="text-xl font-black leading-none text-stone-900">
						{formatMoney(baseCost)}
					</h3>
					<span className="mt-1.5 block text-[10px] font-black uppercase tracking-widest text-stone-400">
						/mês
					</span>
				</div>

				{/* Linha 2 - Manutenção */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Manutenção
						</span>
						<div className="rounded-xl bg-stone-50 p-2.5">
							<CreditCard size={18} className="text-stone-700" />
						</div>
					</div>
					<h3 className="text-xl font-black leading-none text-stone-900">
						{formatMoney(maintenanceCost)}
					</h3>
					<span className="mt-1.5 block text-[10px] font-black uppercase tracking-widest text-stone-400">
						/mês
					</span>
				</div>

				{/* Linha 2 - Subsídio por colaborador */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Subsídio por colaborador
						</span>
						<div className="rounded-xl bg-emerald-50 p-2.5">
							<CreditCard
								size={18}
								className="text-emerald-600"
							/>
						</div>
					</div>

					{subsidyPerEmployee > 0 ? (
						<>
							<h3 className="text-xl font-black leading-none text-stone-900">
								{formatMoney(subsidyPerEmployee)} p/ colaborador
							</h3>

							<div className="mt-3 space-y-2 text-[10px] text-stone-500">
								<div className="flex items-center justify-between gap-3">
									<span>Valor original</span>
									<span className="font-bold text-stone-900">
										{formatMoney(subsidyPerEmployee)}
									</span>
								</div>
								<div className="flex items-center justify-between gap-3">
									<span>
										Total ({plan?.max_employees ?? 0}{' '}
										colabs)
									</span>
									<span className="font-bold text-stone-900">
										{formatMoney(
											subsidyPerEmployee *
												(plan?.max_employees ?? 0),
										)}
									</span>
								</div>
								<div className="pt-1 text-[10px] text-stone-400">
									Total = p/ colaborador × máx. colaboradores
								</div>
							</div>
						</>
					) : (
						<>
							<h3 className="text-xl font-black leading-none text-stone-900">
								Sem subsídio configurado
							</h3>
							<span className="mt-1.5 block text-[10px] font-black uppercase tracking-widest text-stone-400">
								Colaboradores pagam valor integral do clube
							</span>
						</>
					)}
				</div>

				{/* Linha 2 - Valor total */}
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Valor total
						</span>
						<div className="rounded-xl bg-emerald-50 p-2.5">
							<CreditCard
								size={18}
								className="text-emerald-500"
							/>
						</div>
					</div>
					<h3 className="text-xl font-black leading-none text-stone-900">
						{formattedValue}
					</h3>
					<span className="mt-1.5 block text-[10px] font-black uppercase tracking-widest text-stone-400">
						Total estimado por mês
					</span>
				</div>
			</div>
			{/* Estatísticas de funcionários */}
			{employeeStats && (
				<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-start justify-between">
						<span className="text-[10px] font-black uppercase tracking-widest text-stone-300">
							Estatísticas de funcionários
						</span>
						<div className="rounded-xl bg-stone-50 p-2.5">
							<Users size={18} className="text-stone-600" />
						</div>
					</div>
					<div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Total
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.total}
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Ativos
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.active}
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Inativos
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.inactive}
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Vinculados
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.linked}
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Pendentes
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.pending}
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Taxa de vínculo
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.linkRate}
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Assinatura B2B2C
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.subscriptionActiveB2B2C}
							</p>
						</div>
						<div>
							<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
								Assinatura B2C
							</p>
							<p className="mt-0.5 text-lg font-black text-stone-900">
								{employeeStats.subscriptionActiveB2C}
							</p>
						</div>
					</div>
				</div>
			)}
			{company && (
				<SubscribePlanModal
					isOpen={isSubscribePlanModalOpen}
					onClose={() => setIsSubscribePlanModalOpen(false)}
					companyId={company.id}
				/>
			)}
		</section>
	);
};
